/** Quem decide se o guia de uma tela aparece (SPEC-FE-008).

 * A regra é curta e mora aqui, e não em cada tela: aparece quando o recurso
 * está ligado, a tela tem guia, e esta pessoa nunca terminou o dela. Espalhar
 * isso por oito telas daria oito chances de errar a mesma decisão.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { buscarGuias, marcarGuia, mudarGuias, type EstadoDoGuia, type GuiasVistos } from "./api";

export const CHAVE_DOS_GUIAS = ["guias"] as const;

export function useGuias() {
  return useQuery<GuiasVistos>({
    queryKey: CHAVE_DOS_GUIAS,
    queryFn: () => buscarGuias(),
    // Muda uma vez por tela na vida de cada pessoa: reler de minuto em minuto
    // seria consulta para confirmar o que já se sabe.
    staleTime: 10 * 60_000,
    // Falhar aqui não pode quebrar tela nenhuma — sem resposta, nenhum guia
    // aparece, que é o lado inofensivo do erro.
    retry: false,
  });
}

export function useMarcarGuia() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ tela, estado }: { tela: string; estado: EstadoDoGuia }) =>
      marcarGuia(tela, estado),
    // Otimista de propósito: o guia precisa sumir no clique, e não quando a
    // rede responder. Se a gravação falhar, o pior que acontece é ele voltar
    // na próxima visita — e é melhor que uma tela travada esperando.
    onMutate: ({ tela, estado }) => {
      cliente.setQueryData<GuiasVistos>(CHAVE_DOS_GUIAS, (atual) =>
        atual === undefined ? atual : { ...atual, vistos: { ...atual.vistos, [tela]: estado } },
      );
    },
    onSettled: () => cliente.invalidateQueries({ queryKey: CHAVE_DOS_GUIAS }),
  });
}

export function useMudarGuias() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (desligado: boolean) => mudarGuias(desligado),
    onMutate: (desligado) => {
      cliente.setQueryData<GuiasVistos>(CHAVE_DOS_GUIAS, (atual) =>
        atual === undefined ? atual : { ...atual, desligado },
      );
    },
    onSettled: () => cliente.invalidateQueries({ queryKey: CHAVE_DOS_GUIAS }),
  });
}

/** O guia desta tela deve aparecer agora? */
export function deveMostrar(
  guias: GuiasVistos | undefined,
  tela: string,
  temPassos: boolean,
): boolean {
  if (!temPassos) return false;
  // `undefined` é "ainda não sei" — e não "nunca viu". Mostrar o guia antes da
  // resposta o faria piscar para quem já o dispensou.
  if (guias === undefined) return false;
  if (guias.desligado) return false;
  return guias.vistos[tela] === undefined;
}
